import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import BottomSheet from '../../../Components/Ui/BottomSheet';
import { Button } from '../../../Components/MUIs';
import Icon from '../../../Assets/Icon/CustomIcon';
import { filterMembersForAddSheet, normalizeMemberRow } from './emTelUtils';

function CheckboxIcon({ checked }) {
  if (checked) {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <path
          d="M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
          fill="#57B84D"
        />
      </svg>
    );
  }

  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M19 5V19H5V5H19ZM19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z"
        fill="#776D6D"
      />
    </svg>
  );
}

export default function AddContactBottomSheet({
  selectedDepartment,
  memberList = [],
  existingContacts = [],
  isLoading = false,
  departmentScoped = false,
  onClose,
  onComplete,
}) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const normalizedMemberList = useMemo(
    () => (memberList || []).map((row, index) => normalizeMemberRow(row, index)),
    [memberList]
  );

  const filteredMembers = useMemo(
    () =>
      filterMembersForAddSheet(memberList, {
        search,
        selectedDepartment,
        existingContacts,
        departmentScoped,
      }),
    [memberList, selectedDepartment, search, existingContacts, departmentScoped]
  );

  const toggleMember = (memberId) => {
    setSelectedIds((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleComplete = () => {
    const selectedMembers = normalizedMemberList.filter((member) =>
      selectedIds.includes(member.id)
    );
    onComplete?.(selectedMembers);
    onClose?.();
  };

  const emptyMessage = isLoading
    ? '인원 목록을 불러오는 중입니다.'
    : memberList.length === 0
      ? '조회된 인원이 없습니다.'
      : '선택 가능한 인원이 없습니다.';

  return (
    <BottomSheet
      onDismiss={onClose}
      headerText=""
      isHeaderChildren
      snapPoints={({ maxHeight }) => [maxHeight * 0.72, maxHeight * 0.92]}
      defaultSnap={({ snapPoints }) => snapPoints[0]}
      headerChildren={<div className="h-0" />}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(72dvh - 48px)',
          minHeight: 420,
          maxHeight: 640,
          overflow: 'hidden',
          px: 1,
        }}
      >
        <Box sx={{ flexShrink: 0, pb: 2 }}>
          <Typography
            sx={{
              fontSize: '22px',
              fontWeight: 700,
              lineHeight: '28px',
              color: '#1b1c1c',
            }}
          >
            인원 선택
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: '14px', color: '#3e4a3e' }}>
            {selectedDepartment
              ? `[${selectedDepartment}] 부서 비상연락망에 등록할 인원을 선택하세요.`
              : '등록할 부서를 먼저 선택해주세요.'}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: '12px', color: '#776d6d' }}>
            다른 부서 소속 인원도 선택할 수 있습니다.
          </Typography>
        </Box>

        <Box sx={{ position: 'relative', flexShrink: 0, mb: 2 }}>
          <Box
            component="span"
            sx={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#3e4a3e',
              display: 'flex',
            }}
          >
            <Icon.Search width="20" height="20" />
          </Box>
          <Box
            component="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="성명, 직위, 직책, 부서 검색"
            sx={{
              width: '100%',
              height: 48,
              pl: '40px',
              pr: 2,
              border: '1px solid #bdcaba',
              borderRadius: '999px',
              bgcolor: '#f5f3f3',
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box',
              '&:focus': {
                borderColor: '#57B84D',
              },
            }}
          />
        </Box>

        <Box
          data-body-scroll-lock-ignore
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          {filteredMembers.length > 0 ? (
            filteredMembers.map((member) => {
              const isSelected = selectedIds.includes(member.id);

              return (
                <Box
                  key={member.id}
                  component="button"
                  type="button"
                  onClick={() => toggleMember(member.id)}
                  sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    border: isSelected
                      ? '1px solid rgba(87, 184, 77, 0.2)'
                      : '1px solid transparent',
                    borderRadius: '12px',
                    bgcolor: isSelected ? '#f6fbf5' : '#fbf9f8',
                    cursor: 'pointer',
                    textAlign: 'left',
                    '&:hover': {
                      bgcolor: '#f5f3f3',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: '#e9e8e7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Icon.Person width="20" height="20" color="#3e4a3e" />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                        {member.name}{' '}
                        {member.rank && (
                          <Box
                            component="span"
                            sx={{ fontWeight: 400, color: '#3e4a3e' }}
                          >
                            {member.rank}
                          </Box>
                        )}
                      </Typography>
                      {(member.department ||
                        selectedDepartment ||
                        member.position) && (
                        <Typography sx={{ fontSize: '12px', color: '#3e4a3e' }}>
                          {[
                            member.department || selectedDepartment,
                            member.position,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <CheckboxIcon checked={isSelected} />
                </Box>
              );
            })
          ) : (
            <Box
              sx={{
                borderRadius: '12px',
                bgcolor: '#f5f3f3',
                px: 2,
                py: 5,
                textAlign: 'center',
                fontSize: '14px',
                color: '#3e4a3e',
              }}
            >
              {emptyMessage}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            gap: 1,
            pt: 2,
            mt: 'auto',
            borderTop: '1px solid #e4e2e2',
            bgcolor: '#fff',
          }}
        >
          <Button
            name="취소"
            sx={{
              width: '100%',
              height: 36,
              borderRadius: '5px',
              backgroundColor: 'transparent',
              border: '1px solid #776D6D',
              color: '#776D6D',
              boxShadow: 'none',
            }}
            onClick={onClose}
          />
          <Button
            name={`추가 완료 (${selectedIds.length})`}
            disabled={selectedIds.length === 0}
            sx={{
              width: '100%',
              height: 36,
              borderRadius: '5px',
              backgroundColor: '#57B84D',
              color: '#fff',
              boxShadow: 'none',
              '&.Mui-disabled': {
                backgroundColor: '#57B84D',
                color: '#fff',
                opacity: 0.5,
              },
            }}
            onClick={handleComplete}
          />
        </Box>
      </Box>
    </BottomSheet>
  );
}
